import React, {
  useCallback,
  useMemo,
  useState,
  useContext,
  useEffect
} from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Input, Select } from 'api/sharedComponents/reactHookFormComponents';
import {
  EditAppointmentPopup,
  IEditAppointmentPopup,
  ICancellationReason,
  LocationTypes,
  RescheduleModalType
} from 'model';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { Grid, TextArea } from 'semantic-ui-react';
import { attendeeLabelName, formatProviderName } from 'utils/format';
import {
  APPOINTMENT_RESCHEDULE_OFF_SITE_SCHEMA,
  APPOINTMENT_RESCHEDULE_SCHEMA
} from 'utils/validators/calendar';
import PopupActions from 'components/popupActions';
import EventTypeRadioGroup from './EventTypeRadioGroup';
import ClientRow from './ClientRow';
import { mapToOption } from 'utils/mappers/form';
import FormItem from 'api/sharedComponents/reactHookFormComponents/formItem';
import { useLazyQuery, useMutation, useApolloClient } from '@apollo/react-hooks';
import { useReactiveVar } from '@apollo/client';
import { HAS_CONVERTED_EVENT_IN_SERIES } from 'api/graphql/v2/queries/Events';
import {
  CONVERTED_EVENT_MESSAGE,
  CONVERTED_SERIES_MESSAGE
} from 'utils/downstreams/DownStreamModalConfigurations';
import {
  calendarEventsPrefVar,
  clientsResourcesPrevVar,
  providerResourcesPrevVar
} from 'utils/cache/filters';
import { plannerContext } from 'pages/MainCalendarPage';
import { ICalendarEvent } from 'model/calendar/events';
import OffsiteLocation from 'views/components/forms/appointment/components/Location/OffsiteLocation';
import { mainCalendarPrefDayVar, PlannerView } from 'utils/cache/calendar';
import { ICalendarResource } from 'model/calendar/filters';
import { ContentWrapper } from './style';
import TimeRange from './TimeRange';
import { getCurrentClinicsOptionsV2 } from 'views/components/forms/appointment/components/MainFields/appointmentClinicHook';
import { Link } from 'react-router-dom';
import { ATHENA_DEFAULT_DELETE_REASON } from 'utils/constants/lists';
import {
  mapEventDataToBeRescheduled,
  getDefaultPopupFormValues
} from 'utils/mappers/calendar';
import moment from 'moment';
import { IProvider, IClient, IFullEvent, IEvent } from 'model/v2';
import { useClinicDataMap } from 'api/graphql/v2/hooks/clinics';
import Content from 'views/components/ui/content';
import { UPDATE_EVENT } from 'api/graphql/v2/mutations/Event';
import { EventType } from 'model/calendar/events';
import { prepareEventsAfterReschedule } from 'utils/mappers/response/eventsV2';
import { FEATURES, getFeatureAvailability } from 'utils/featureToggle';
import { WarningOutlined, EyeOutlined } from '@ant-design/icons';
import {
  DEFAULT_DOCTOR_IMAGE,
  DEFAULT_RESCHEDULE_NOTE
} from 'utils/constants/default';
import ObservationRow from 'pages/MainCalendarPage/AppointmentObserver/ObservationRow';
import ImpactedObservers from 'pages/MainCalendarPage/AppointmentModals/RescheduleModal/ImpactedObservers';
import ObserverForm from './ObserverForm';
import AbaImpactedObservers from 'pages/MainCalendarPage/AppointmentModals/RescheduleModal/AbaImpactedObservers';
import {
  VALIDATE_LOCATIONS,
  VALIDATE_MEAL_WAIVER
} from 'api/graphql/v2/queries/Location';
import { ImpactedObserversWrapper } from './style';
import LocationValidatorAlert from 'pages/MainCalendarPage/AppointmentView/AttendeesView/LocationValidatorAlert';
import MealWaiverAlert from 'pages/MainCalendarPage/AppointmentView/AttendeesView/MealWaiverAlert';

interface IProps {
  fullEvent: IFullEvent;
  provider?: IProvider;
  oldProvider?: IProvider;
  client?: IClient;
  isConflictingProvider: boolean;
  isSeries: boolean;
  newStartDate?: string;
  newEndDate?: string;
  oldStartDate?: string;
  oldEndDate?: string;
  view: PlannerView;
  onClose: () => void;
  onCancel: () => void;
  cancellationReasons?: ICancellationReason[];
  recurringEventsCount?: number;
  deleteWhenCancelAppointmentsIds?: number[];
  observationOfLeadEvent?: IEvent[];
  observersOfEachEvent?: any;
}

const ClientForm: React.FC<IProps> = ({
  fullEvent,
  isSeries,
  provider,
  oldProvider,
  client,
  isConflictingProvider,
  newStartDate,
  newEndDate,
  oldStartDate,
  oldEndDate,
  view,
  onClose,
  onCancel,
  cancellationReasons,
  recurringEventsCount,
  deleteWhenCancelAppointmentsIds,
  observationOfLeadEvent
}: IProps) => {
  const { clinicsMap, loadingClinics } = useClinicDataMap('Edit');
  const [isSelectedReason, setIsSelectedReason] = useState(false);
  const [alertContent, setAlertContent] = React.useState<any>(null);
  const [mealWaiverAlertContent, setMealWaiverAlertContent] = React.useState<
    string | null
  >(null);
  const [hasMealWaiver, setHasMealWaiver] = React.useState<boolean>(false);
  const events = useReactiveVar<ICalendarEvent[]>(calendarEventsPrefVar);
  const providers = useReactiveVar<ICalendarResource[]>(
    providerResourcesPrevVar
  );
  const [seriesEventIndex, setSeriesEventIndex] = useState(0);
  const { ShadowFeatureAvailability } = useContext(plannerContext);
  const currentEventInSeries = useMemo(() => {
    if (observationOfLeadEvent) return observationOfLeadEvent[seriesEventIndex];
    return undefined;
  }, [observationOfLeadEvent, seriesEventIndex]);
  const providerName = provider?.hasOwnProperty('attendeeType')
    ? attendeeLabelName(provider.displayName) ||
      formatProviderName({ ...provider, displayName: undefined })
    : formatProviderName(provider);
  const isConverted = fullEvent.isConverted;
  const next = useCallback(() => {
    setSeriesEventIndex(currentEventInSeries =>
      currentEventInSeries < observationOfLeadEvent!.length - 1
        ? currentEventInSeries + 1
        : currentEventInSeries
    );
  }, [observationOfLeadEvent]);
  const previous = useCallback(
    () =>
      setSeriesEventIndex(currentEventInSeries =>
        currentEventInSeries > 0
          ? currentEventInSeries - 1
          : currentEventInSeries
      ),
    []
  );
  console.log(
    observationOfLeadEvent,
    previous,
    next,
    'observationOfLeadEvent in client form'
  );

  const [
    validateAppointment,
    { loading: locationValidationInProgress }
  ] = useLazyQuery(VALIDATE_LOCATIONS, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const results = data.checkLocationAvailability;
      setAlertContent(results.alertMessage);
    },
    onError: error => {
      console.error('Query Error:', error.message);
    }
  });

  const [
    validateMealWaiver,
    { loading: mealWaiverValidationInProgress }
  ] = useLazyQuery(VALIDATE_MEAL_WAIVER, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const results = data.checkMealWaiverAlert;
      if (results.showAlert) {
        setMealWaiverAlertContent(results.alertMessage);
        setHasMealWaiver(results.hasMealWaiver);
      } else {
        setMealWaiverAlertContent(null);
      }
    },
    onError: error => {
      console.error('Meal waiver query error:', error.message);
    }
  });

  const clinicId = fullEvent.event.clinic?.id;

  useEffect(() => {
    setAlertContent(null);
    setMealWaiverAlertContent(null);
    const { startDate, endDate, clinic, locationType } = fullEvent.event;
    const providerId = provider?.id;
    const eventId = fullEvent.event.id;

    const validateAppointmentVariables = {
      variables: {
        providerId,
        locationType,
        startTime: moment(startDate).toISOString(),
        endTime: moment(endDate).toISOString(),
        selectedClinicId: clinic?.id
      }
    };

    if (providerId && startDate && endDate && clinic?.id && locationType) {
      validateAppointment(validateAppointmentVariables);
    }

    // Meal waiver validation
    if (providerId && startDate && endDate) {
      const mealWaiverVariables: any = {
        providerId,
        startTime: moment(startDate).toISOString(),
        endTime: moment(endDate).toISOString()
      };

      // Include eventId to exclude the current event from calculations (since we're rescheduling it)
      if (eventId !== undefined && eventId !== null) {
        mealWaiverVariables.eventId = eventId;
      }

      validateMealWaiver({ variables: mealWaiverVariables });
    }
  }, [
    fullEvent.event,
    clinicId,
    provider,
    validateAppointment,
    validateMealWaiver
  ]);

  const [selectedRadioButton, setSelectedRadioButton] = useState(null);
  const [seriesConversionError, setSeriesConversionError] = useState<
    string | null
  >(null);
  let observersArray: any = [];
  let observersCount: any;
  if (selectedRadioButton == 2) {
    observationOfLeadEvent!.map(eachEvent => {
      observersArray.push(eachEvent.observations?.length);
      return null;
    });
    observersCount = observersArray.reduce((a: any, b: any) => a! + b!, 0);
  } else {
    observersCount = fullEvent?.event?.observationsCount!;
  }
  const eventTypes = ['ABA', 'DT'];
  const eventType = fullEvent.event.appointmentType?.eventType?.name!;
  const isABAOrDTEvent = eventTypes.includes(eventType);
  const getInitalAbaObserversList = () => {
    return fullEvent.event.observations
      ? fullEvent.event.observations.map(observation => ({
          id: observation.id,
          provider: observation.provider,
          locationType: observation.locationType,
          telehealthLink: observation.telehealthLink,
          startDate: observation.startDate,
          endDate: observation.endDate,
          duration: observation.duration
        }))
      : [];
  };
  let abaObserversListNew = getInitalAbaObserversList();
  const eventObserverduration = moment
    .duration(moment(newStartDate).diff(moment(oldStartDate)))
    .asMinutes();
  abaObserversListNew = abaObserversListNew.map(obj => {
    const momentNewStartDate = moment(obj.startDate).add(
      eventObserverduration,
      'minutes'
    );
    const momentNewEndDate = moment(momentNewStartDate).add(
      obj.duration,
      'minutes'
    );
    return {
      ...obj,
      startDate: momentNewStartDate,
      endDate: momentNewEndDate,
      provider: { id: obj.provider?.id, name: obj.provider?.name }
    };
  });

  let [abaObserversList, setAbaObserversList] = React.useState<IEvent[]>(
    abaObserversListNew
  );
  let seriesObserverEvent = [];
  if (observationOfLeadEvent) {
    seriesObserverEvent = JSON.parse(JSON.stringify(observationOfLeadEvent!));
  }
  const seriesEventObservationsArr = seriesObserverEvent?.map(
    (observerEvent: {
      startDate: moment.MomentInput;
      endDate: moment.MomentInput;
      observations: any[];
    }) => {
      observerEvent.startDate = moment(observerEvent.startDate).add(
        eventObserverduration,
        'minutes'
      );
      observerEvent.endDate = moment(observerEvent.endDate).add(
        eventObserverduration,
        'minutes'
      );
      const observations = observerEvent.observations?.map(obj => {
        const momentNewStartDate = moment(obj.startDate).add(
          eventObserverduration,
          'minutes'
        );
        const momentNewEndDate = moment(momentNewStartDate).add(
          obj.duration,
          'minutes'
        );
        return {
          ...obj,
          startDate: momentNewStartDate,
          endDate: momentNewEndDate,
          provider: { id: obj.provider?.id, name: obj.provider?.name }
        };
      });
      return {
        ...observerEvent,
        observations: observations
      };
    }
  );
  let [seriesEventObservations, setSeriesEventObservations] = React.useState<
    IEvent[]
  >(seriesEventObservationsArr!);
  const radioButtonStatus = (index: any) => {
    setSelectedRadioButton(index);
    // setSeriesEventObservations(seriesEventObservationsArr!);
    // setAbaObserversList(abaObserversListNew);
    if (index == 2) {
      isAlertShownSet = getSeriesAbaObserverValidationStatus(
        seriesEventObservations,
        newStartDate,
        newEndDate
      );
    } else {
      isAlertShownSet = getAbaObserverValidationStatus(
        abaObserversList,
        newStartDate,
        newEndDate
      );
    }
    setIsValidated(isAlertShownSet);
  };
  fullEvent.event.startDate = moment(newStartDate).toDate();
  fullEvent.event.endDate = moment(newEndDate).toDate();

  const [lastAbaObserver, setLastAbaObserver] = React.useState<IEvent>({});
  console.log(lastAbaObserver);

  const clients = useReactiveVar<ICalendarResource[]>(clientsResourcesPrevVar);
  const calendarDate = useReactiveVar(mainCalendarPrefDayVar);
  const methods = useForm<IEditAppointmentPopup>({
    defaultValues: getDefaultPopupFormValues(
      fullEvent?.event?.clinic?.id || undefined,
      fullEvent?.event?.room?.id || undefined,
      fullEvent?.event?.address,
      fullEvent?.event?.locationCategory,
      fullEvent?.event?.telehealthLink
    ),
    resolver: yupResolver(
      fullEvent?.event?.locationType === (LocationTypes.offSite as any)
        ? APPOINTMENT_RESCHEDULE_OFF_SITE_SCHEMA(
            deleteWhenCancelAppointmentsIds,
            fullEvent?.event?.appointmentType?.id!
          )
        : APPOINTMENT_RESCHEDULE_SCHEMA(
            deleteWhenCancelAppointmentsIds,
            fullEvent?.event?.appointmentType?.id!
          )
    )
  });

  const {
    handleSubmit,
    errors,
    formState: { isSubmitting },
    setValue
  } = methods;

  const { room, type } = useWatch({
    name: ['room', 'type'],
    control: methods.control
  });

  // Clear the converted-event block (and re-enable Confirm) when the user
  // switches between "only this event" and "update series", since each affects
  // a different set of events.
  useEffect(() => {
    setSeriesConversionError(null);
  }, [type]);

  const [updateEvent] = useMutation(UPDATE_EVENT);
  const apolloClient = useApolloClient();

  const onConfirm = handleSubmit(async (data: IEditAppointmentPopup) => {
    // Re-check conversion status at confirm time. `fullEvent.isConverted` only
    // reflects the state when the modal was opened. When rescheduling the whole
    // series, the affected set also includes future events not represented by
    // that single flag, so ask the backend to check the whole series and block
    // (keeping the modal open with an inline error) if any is converted.
    setSeriesConversionError(null);
    const isSeriesReschedule =
      data.type === RescheduleModalType.recurringMappedValue;
    try {
      const { data: seriesData } = await apolloClient.query({
        query: HAS_CONVERTED_EVENT_IN_SERIES,
        variables: {
          ids: [fullEvent.event.id],
          recurrent: isSeriesReschedule
        },
        fetchPolicy: 'network-only'
      });
      if (seriesData?.hasConvertedEventInSeries) {
        setSeriesConversionError(
          isSeriesReschedule ? CONVERTED_SERIES_MESSAGE : CONVERTED_EVENT_MESSAGE
        );
        return;
      }
    } catch (error) {
      // If the check fails we fall through and let the reschedule attempt
      // proceed, surfacing any real error through the mutation's catch below.
    }

    const rawEventData = mapEventDataToBeRescheduled(
      fullEvent,
      data,
      newStartDate,
      newEndDate,
      provider,
      oldProvider,
      clinicsMap
    );

    // Keep `room` only if in-clinic AND we have a room id
    const isInClinic =
      rawEventData.locationType === (LocationTypes.inClinic as any);
    const roomId = rawEventData?.room?.id;
    const { room: _omitRoom, ...base } = rawEventData as any;
    const eventData =
      isInClinic && roomId ? { ...base, room: { id: roomId } } : base;

    // Observers payload
    const updatedObserversData =
      selectedRadioButton === 2
        ? (seriesEventObservations ?? []).flatMap(e => e.observations ?? [])
        : abaObserversList;

    const submittedData = {
      event: eventData,
      recurrent: data.type === RescheduleModalType.recurringMappedValue,
      cancelNote: data.notes,
      cancelReasonId: data.reason || ATHENA_DEFAULT_DELETE_REASON,
      // Audit note for the EDIT_TIME history row (direct-edit reschedule), shown
      // in History alongside the cancel reason. Prefer the note the user typed
      // in the reschedule modal; else the default marker.
      notes: data.notes || DEFAULT_RESCHEDULE_NOTE,
      updatedObserversData: updatedObserversData
    };

    try {
      const res = await updateEvent({ variables: { data: submittedData } });
      const updatedEvent = res.data?.updateEvent;
      if (updatedEvent) {
        const currentEvents = prepareEventsAfterReschedule(
          updatedEvent,
          events,
          providers,
          clients,
          view,
          calendarDate,
          fullEvent.event,
          isSeriesReschedule
        );
        calendarEventsPrefVar(currentEvents);
        // NOTE: do NOT call handleDateSelect here — it sets mainCalendarPrefDayVar,
        // which triggers a no-cache refetch that replaces calendarEventsPrefVar and
        // wipes the optimistic isCancelInProgress flag (and the new event) set just
        // above. The optimistic update + calendarEvents subscription reconcile the
        // planner without it.
        onClose();
      }
    } catch (error) {
      console.log('Error while updating event ::', JSON.stringify(error));
      onCancel();
    }
  });

  const { clinicOptionsList } = useMemo(
    () => getCurrentClinicsOptionsV2(client, clinicsMap),
    [client, clinicsMap]
  );

  const handleClinicChange = useCallback(
    newClinicId => {
      //TODO:PLANNER_MAPPING LocationType type
      setAlertContent(null);
      if (fullEvent?.event.locationType === (LocationTypes.inClinic as any)) {
        const { startDate, endDate, locationType } = fullEvent.event;
        const providerId = provider?.id;
        const validateAppointmentVariables = {
          variables: {
            providerId,
            locationType,
            startTime: moment(startDate).toISOString(),
            endTime: moment(endDate).toISOString(),
            selectedClinicId: newClinicId
          }
        };
        if (providerId && startDate && endDate && newClinicId && locationType) {
          validateAppointment(validateAppointmentVariables);
        }
      }
    },
    [
      fullEvent,
      clinicsMap,
      room,
      setValue,
      validateAppointment,
      provider,
      alertContent
    ]
  );

  const reasonsOptionsList = useMemo(
    () =>
      cancellationReasons
        ? mapToOption(
            cancellationReasons?.map(reason => {
              return {
                id: reason.reasonId,
                value: reason.name
              };
            })
          )
        : [],
    [cancellationReasons]
  );

  const isDeleteEvent = useMemo(() => {
    return (
      deleteWhenCancelAppointmentsIds?.includes(
        fullEvent?.event?.appointmentType?.id!
      ) || type === RescheduleModalType.recurringMappedValue
    );
  }, [deleteWhenCancelAppointmentsIds, fullEvent.event.appointmentType, type]);

  const handleChangeReason = (e: any) => {
    if (e.value !== '') {
      setIsSelectedReason(true);
    }
  };
  let isAlertShownSet = false;
  if (selectedRadioButton == 2) {
    isAlertShownSet = getSeriesAbaObserverValidationStatus(
      seriesEventObservations,
      newStartDate,
      newEndDate
    );
  } else {
    isAlertShownSet = getAbaObserverValidationStatus(
      abaObserversList,
      newStartDate,
      newEndDate
    );
  }
  const [isValidated, setIsValidated] = useState<boolean>(isAlertShownSet);
  return (
    <ContentWrapper>
      <FormProvider {...methods}>
        <Content loading={loadingClinics} data={true} error={undefined}>
          {() => (
            <Grid columns={2}>
              <Grid.Row>
                <label className="title">
                  Confirm Reschedule:{' '}
                  {fullEvent?.event?.appointmentType?.parent
                    ? fullEvent?.event?.appointmentType?.parent.title
                    : fullEvent?.event?.appointmentType?.title}
                </label>
              </Grid.Row>
              {selectedRadioButton == 2 && (
                <>
                  <Grid.Row>
                    <label className="reschedule-message-warning">
                      Are you sure you want to reschedule the series?
                    </label>
                  </Grid.Row>
                  <Grid.Row>
                    {selectedRadioButton == 2 &&
                    observersCount > 0 &&
                    !isABAOrDTEvent ? (
                      <label className="impacted-observers-heading">
                        Impacted Observers:
                      </label>
                    ) : (
                      <></>
                    )}
                  </Grid.Row>
                  <Grid.Row className="Impacted-Observers">
                    {observationOfLeadEvent &&
                      observersCount > 0 &&
                      (observationOfLeadEvent.length > 0 &&
                      selectedRadioButton == 2 &&
                      !isABAOrDTEvent ? (
                        observationOfLeadEvent.map(
                          (observationEvent: IEvent, index: number) => {
                            return (
                              <ImpactedObservers
                                observationEvent={observationEvent.observations}
                                eventIndex={index}
                              />
                            );
                          }
                        )
                      ) : (
                        <></>
                      ))}
                  </Grid.Row>
                </>
              )}
              <Grid.Row>
                <label className="label subHeader">Attendees:</label>
              </Grid.Row>
              <Grid.Row>
                <ClientRow
                  userId={
                    fullEvent?.event?.type === EventType.adminEvent
                      ? provider?.id
                      : client?.id
                  }
                  alt={
                    fullEvent?.event?.type === EventType.adminEvent
                      ? providerName
                      : client?.name
                  }
                  clientName={client?.name}
                  abbreviation={fullEvent?.event?.clinic?.abbreviation}
                  email={provider?.email}
                />
              </Grid.Row>
              <Grid.Row>
                <div className="name-wrapper">
                  <div>
                    <Link
                      className="name"
                      target="_blank"
                      to={`/providers/${provider?.id}`}
                    >
                      <img
                        src={DEFAULT_DOCTOR_IMAGE}
                        alt={'pic'}
                        className="client-pic"
                      />
                      {provider?.hasOwnProperty('attendeeType')
                        ? providerName
                        : `${providerName}, ${
                            fullEvent?.event?.provider?.speciality?.abbreviation
                          } (${
                            fullEvent?.event?.provider?.clinic?.abbreviation
                          })`}
                    </Link>
                    {isConflictingProvider && (
                      <WarningOutlined className="icon" />
                    )}
                  </div>
                  <div className="attende-type">
                    <EyeOutlined className="type-icon" />
                    <label> Provider </label>
                  </div>
                </div>
              </Grid.Row>
              {ShadowFeatureAvailability &&
                (fullEvent.event.observationsCount !== undefined &&
                fullEvent.event.observationsCount > 0 &&
                !isABAOrDTEvent ? (
                  <ObservationRow
                    observations={currentEventInSeries?.observations}
                  />
                ) : (
                  <></>
                ))}
              {newStartDate &&
                newEndDate &&
                isABAOrDTEvent &&
                selectedRadioButton != 2 &&
                abaObserversList.map((Observations: IEvent, index: number) => (
                  <ObserverForm
                    key={index}
                    observerIndex={index + 1}
                    leadEvent={fullEvent.event}
                    observations={Observations}
                    setAbaObserversList={setAbaObserversList}
                    abaObserversList={abaObserversList}
                    setIsValidated={setIsValidated}
                    newStartDate={newStartDate}
                    newEndDate={newEndDate}
                    setLastAbaObserver={setLastAbaObserver}
                    isValidated={isValidated}
                    initialValidation={true}
                    setSeriesEventObservations={setSeriesEventObservations}
                  ></ObserverForm>
                ))}
              <Grid.Row>
                <Grid.Column>
                  <div style={{ marginTop: '10px' }}></div>
                  <label className="label subHeader">Event Details:</label>
                </Grid.Column>
                {isSeries && (
                  <Grid.Column>
                    <Grid.Row className={'observationBtn-row'}>
                      <Grid.Column width={1}>
                        {seriesEventIndex !== 0 && <></>}
                      </Grid.Column>
                    </Grid.Row>
                  </Grid.Column>
                )}
              </Grid.Row>
              <Grid.Row className="date">
                <TimeRange start={newStartDate!} end={newEndDate!} />
              </Grid.Row>
              <Grid.Row className="date">
                <Grid.Column>
                  <label className="label subHeader">Location</label>
                </Grid.Column>{' '}
              </Grid.Row>
              {fullEvent?.event?.locationType !== LocationTypes.offSite && (
                <Grid.Row className="row-maxWidth">
                  <FormItem>
                    <Controller
                      name={`${EditAppointmentPopup.clinic}.id`}
                      render={fieldProps => (
                        <Select
                          options={clinicOptionsList}
                          className="rooms-dropdown selected"
                          placeholder="Select Clinic"
                          field={fieldProps}
                          errors={errors}
                          onSelect={handleClinicChange}
                        />
                      )}
                    />
                  </FormItem>
                </Grid.Row>
              )}
              {fullEvent?.event?.locationType === LocationTypes.offSite && (
                <Grid.Row className="row-maxWidth reschedule-location-row">
                  <OffsiteLocation clientId={client?.id} />
                </Grid.Row>
              )}
              {fullEvent?.event?.locationType === LocationTypes.teleHealth && (
                <Grid.Row className="row-maxWidth">
                  <FormItem>
                    <Controller
                      name={EditAppointmentPopup.telehealthLink}
                      render={fieldProps => (
                        <Input
                          field={fieldProps}
                          className="input"
                          placeholder="Add Tele Health Link"
                          errors={errors}
                        />
                      )}
                    />
                  </FormItem>
                </Grid.Row>
              )}
              {isSeries && (
                <>
                  <Grid.Row>
                    <label className="label subHeader">Recurrence:</label>
                  </Grid.Row>
                  <Grid.Row className="radio-btns">
                    <Controller
                      name={EditAppointmentPopup.type}
                      render={() => (
                        <EventTypeRadioGroup
                          methods={methods}
                          recurringEventsCount={recurringEventsCount}
                          radioButtonStatus={radioButtonStatus}
                          observersCount={observersCount}
                        />
                      )}
                    />
                  </Grid.Row>
                  <Grid.Row>
                    {selectedRadioButton == 2 &&
                    isABAOrDTEvent &&
                    seriesEventObservations.length > 0 ? (
                      <label className="impacted-observers-heading">
                        Impacted Observers:
                      </label>
                    ) : (
                      <></>
                    )}
                  </Grid.Row>
                  <Grid.Row className="Impacted-Observers">
                    {observationOfLeadEvent &&
                      observersCount > 0 &&
                      (selectedRadioButton == 2 &&
                      isABAOrDTEvent &&
                      seriesEventObservations.length > 0 ? (
                        seriesEventObservations?.map(
                          (observationEvent: IEvent, index: number) => {
                            return (
                              observationEvent.observations?.length! > 0 && (
                                <AbaImpactedObservers
                                  observationEvent={
                                    observationEvent.observations
                                  }
                                  eventIndex={index}
                                  setSeriesEventObservations={
                                    setSeriesEventObservations
                                  }
                                  seriesEventObservations={
                                    seriesEventObservations
                                  }
                                  leadEvent={observationEvent}
                                  setIsValidated={setIsValidated}
                                  isValidated={isValidated}
                                  newStartDate={newStartDate!}
                                  newEndDate={newEndDate!}
                                  initialValidation={true}
                                  setAbaObserversList={setAbaObserversList}
                                  eventType={
                                    fullEvent.event.appointmentType?.eventType
                                      ?.name!
                                  }
                                />
                              )
                            );
                          }
                        )
                      ) : (
                        <></>
                      ))}
                  </Grid.Row>
                </>
              )}

              {(!isDeleteEvent ||
                !getFeatureAvailability(FEATURES.RESCHEDULE_MODAL_UPDATES)) && (
                <>
                  <Grid.Row className="row-maxWidth">
                    <FormItem>
                      <Controller
                        name={EditAppointmentPopup.reason}
                        render={fieldProps => (
                          <Select
                            showSearch
                            onSelect={handleChangeReason}
                            options={reasonsOptionsList}
                            className={
                              isSelectedReason
                                ? 'rooms-dropdown selected'
                                : 'rooms-dropdown'
                            }
                            field={fieldProps}
                            filterOption={(
                              input: string | undefined,
                              option: { children: string }
                            ) =>
                              option.children
                                ?.toLowerCase()
                                .includes(input?.toLowerCase() ?? '')
                            }
                            listHeight={162}
                            placeholder={
                              !isSeries ||
                              type === RescheduleModalType.singleMappedValue
                                ? 'Select Reason'
                                : 'Select Reason (Optional)'
                            }
                            errors={errors}
                          />
                        )}
                      />
                    </FormItem>
                  </Grid.Row>
                  <Grid.Row className="row-maxWidth">
                    <FormItem>
                      <Controller
                        name={EditAppointmentPopup.notes}
                        as={
                          <TextArea
                            className="text-area"
                            placeholder="Add Note (Optional)"
                            errors={errors}
                          />
                        }
                      />
                    </FormItem>
                  </Grid.Row>
                </>
              )}
              <Grid.Row>
                <label className="label subHeader">Previously</label>
              </Grid.Row>
              {oldProvider && (
                <Grid.Row>
                  <div className="name-wrapper">
                    <Link
                      className="name"
                      target="_blank"
                      to={`/providers/${oldProvider.id}`}
                    >
                      {`${formatProviderName(oldProvider)}, ${oldProvider?.speciality?.abbreviation} (${oldProvider?.clinic?.abbreviation})`}
                    </Link>
                  </div>
                </Grid.Row>
              )}
              <Grid.Row className="date">
                <TimeRange
                  start={oldStartDate!}
                  end={oldEndDate!}
                  oneLineFormat
                />
              </Grid.Row>
              {fullEvent?.event?.locationType === LocationTypes.offSite && (
                <Grid.Row className="name-wrapper">
                  <label>{`${fullEvent?.event?.address?.name}, ${fullEvent?.event?.address?.addressLine1}, ${fullEvent?.event?.address?.city}, ${fullEvent?.event?.address?.state}, ${fullEvent?.event?.address?.zipCode}`}</label>
                </Grid.Row>
              )}
              {fullEvent?.event?.locationType === LocationTypes.teleHealth && (
                <Grid.Row className="name-wrapper">
                  <label>{fullEvent?.event?.telehealthLink}</label>
                </Grid.Row>
              )}
              {isConverted && (
                <Grid.Row className="name-wrapper">
                  <p style={{ color: '#e82424', marginTop: '20px' }}>
                    This event is converted in Bloom and it cannot
                    be rescheduled.
                  </p>
                </Grid.Row>
              )}

              {alertContent && (
                <ImpactedObserversWrapper>
                  <LocationValidatorAlert
                    alertContent={alertContent}
                    setAlertContent={setAlertContent}
                  />
                </ImpactedObserversWrapper>
              )}
              {mealWaiverAlertContent && (
                <ImpactedObserversWrapper>
                  <MealWaiverAlert
                    alertContent={mealWaiverAlertContent}
                    hasMealWaiver={hasMealWaiver}
                    setAlertContent={setMealWaiverAlertContent}
                  />
                </ImpactedObserversWrapper>
              )}
              {seriesConversionError && (
                <Grid.Row>
                  <label
                    className="reschedule-conversion-error"
                    style={{
                      color: '#db2828',
                      width: '100%',
                      padding: '0 15px',
                      textAlign: 'center',
                      fontSize: '13px',
                      lineHeight: 1.35
                    }}
                  >
                    {seriesConversionError}
                  </label>
                </Grid.Row>
              )}
              <Grid.Row className="modal-actions">
                <PopupActions
                  disabled={
                    isSubmitting ||
                    isValidated ||
                    isConverted! ||
                    !!seriesConversionError ||
                    locationValidationInProgress ||
                    mealWaiverValidationInProgress
                  }
                  loading={isSubmitting}
                  onConfirm={onConfirm}
                  onCancel={onCancel}
                />
              </Grid.Row>
            </Grid>
          )}
        </Content>
      </FormProvider>
    </ContentWrapper>
  );
};

export default React.memo(ClientForm);

export const getSeriesAbaObserverValidationStatus = (
  seriesEventObservations: any[],
  newStartDate: moment.MomentInput | undefined,
  newEndDate: moment.MomentInput | undefined
) => {
  let isAlertShownSet = false;
  seriesEventObservations.map(observationEvent => {
    observationEvent?.observations?.map(
      (eventObj: {
        startDate: moment.MomentInput | undefined;
        endDate: moment.MomentInput | undefined;
        duration: any;
      }) => {
        const observerStartTime = moment(
          moment(eventObj.startDate).format('HH:mm:ss'),
          'HH:mm:ss'
        );
        const observerEndTime = moment(
          moment(eventObj.endDate).format('HH:mm:ss'),
          'HH:mm:ss'
        );
        const eventStartTime = moment(
          moment(newStartDate).format('HH:mm:ss'),
          'HH:mm:ss'
        );
        const eventEndTime = moment(
          moment(newEndDate).format('HH:mm:ss'),
          'HH:mm:ss'
        );
        if (
          observerStartTime.isBefore(eventStartTime) ||
          observerStartTime.isAfter(eventEndTime) ||
          observerEndTime.isBefore(eventStartTime) ||
          observerEndTime.isAfter(eventEndTime)
        ) {
          isAlertShownSet = true;
        } else if (eventObj?.duration! < 15) {
          isAlertShownSet = true;
        }
      }
    );
  });
  return isAlertShownSet;
};

export const getAbaObserverValidationStatus = (
  abaObserversList: any[],
  newStartDate: moment.MomentInput | undefined,
  newEndDate: moment.MomentInput | undefined
) => {
  let isAlertShownSet = false;
  abaObserversList.map(eventObj => {
    const observerStartTime = moment(
      moment(eventObj.startDate).format('HH:mm:ss'),
      'HH:mm:ss'
    );
    const observerEndTime = moment(
      moment(eventObj.endDate).format('HH:mm:ss'),
      'HH:mm:ss'
    );
    const eventStartTime = moment(
      moment(newStartDate).format('HH:mm:ss'),
      'HH:mm:ss'
    );
    const eventEndTime = moment(
      moment(newEndDate).format('HH:mm:ss'),
      'HH:mm:ss'
    );
    if (
      observerStartTime.isBefore(eventStartTime) ||
      observerStartTime.isAfter(eventEndTime) ||
      observerEndTime.isBefore(eventStartTime) ||
      observerEndTime.isAfter(eventEndTime)
    ) {
      isAlertShownSet = true;
    } else if (eventObj?.duration! < 15) {
      isAlertShownSet = true;
    }
  });
  return isAlertShownSet;
};
